import {
  type StoreParamsAsKey,
  useStoreParams,
} from '@/features/shared/utils/dataFetching/storeParams'
import { minutesToMilliseconds } from 'date-fns/minutesToMilliseconds'
import { queryOptions, useQuery } from '@tanstack/react-query'
import {
  fetchSubstitutionPreference,
  fetchSubstitutionPreferences,
  fetchSubstitutions,
  fetchSubstitutionsForProducts,
} from '@/features/shop/services/Substitutions/fetchers'
import { useUserId } from '@/features/account/services/User/hooks'
import { getSortedProductIds } from '@/features/shop/services/Product/utils'
import { getSubstitutionPreferenceKey } from '@/features/shop/services/Substitutions/keys'

type ProductForSubstitutions = {
  id: number
}

export const SubstitutionsQuery = 'Substitutions'

type ProductIdAndStoreParamsAsKey = StoreParamsAsKey & { productId: number }
type ProductIdsAndStoreParamsAsKey = StoreParamsAsKey & { productIds: number[] }

export const substitutionPreferenceQueryOptions = (
  params: Parameters<typeof getSubstitutionPreferenceKey>[0],
) =>
  queryOptions({
    queryKey: getSubstitutionPreferenceKey(params),
    queryFn: fetchSubstitutionPreference,
    staleTime: minutesToMilliseconds(10),
  })

export const useQueryPreference = (productId: number) => {
  const storeParams = useStoreParams()
  const userId = useUserId()
  const { store_location_id } = storeParams

  return useQuery({
    ...substitutionPreferenceQueryOptions({ productId, storeParams }),
    enabled: Boolean(userId && store_location_id && productId),
  })
}

export const SubstitutionPreferencesQuery = 'Substitution Preferences'
export type SubstitutionPreferencesQueryKey = readonly [
  typeof SubstitutionPreferencesQuery,
  ProductIdsAndStoreParamsAsKey,
]
const substitutionPreferencesQueryOptions = (params: ProductIdsAndStoreParamsAsKey) =>
  queryOptions({
    queryKey: [SubstitutionPreferencesQuery, params] as const,
    queryFn: fetchSubstitutionPreferences,
  })

export const useQueryPreferences = ({
  products,
  enabled = true,
}: {
  products: ProductForSubstitutions[]
  enabled?: boolean
}) => {
  const storeParams = useStoreParams()
  const { store_location_id } = storeParams
  // filter out special request products and sort by product id
  const productIds = getSortedProductIds(products)

  return useQuery({
    ...substitutionPreferencesQueryOptions({
      storeParams,
      productIds,
    }),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(store_location_id && enabled && productIds.length > 0),
  })
}

export type SubstitutionsQueryKey = readonly [
  typeof SubstitutionsQuery,
  ProductIdAndStoreParamsAsKey,
]
export const substitutionsQueryOptions = (params: ProductIdAndStoreParamsAsKey) =>
  queryOptions({
    queryKey: [SubstitutionsQuery, params] as const,
    queryFn: fetchSubstitutions,
    staleTime: minutesToMilliseconds(10),
  })

export const useQuerySubstitutions = ({
  productId,
  enabled = true,
}: {
  productId: number
  enabled?: boolean
}) => {
  const storeParams = useStoreParams()

  return useQuery({
    ...substitutionsQueryOptions({ productId, storeParams }),
    enabled: Boolean(enabled && storeParams.store_location_id),
  })
}

export type SubstitutionsForProductsQueryKey = readonly [
  typeof SubstitutionsQuery,
  ProductIdsAndStoreParamsAsKey,
]
const substitutionsForProductsQueryOptions = (params: ProductIdsAndStoreParamsAsKey) =>
  queryOptions({
    queryKey: [SubstitutionsQuery, params],
    queryFn: fetchSubstitutionsForProducts,
    staleTime: minutesToMilliseconds(10),
  })

export const useQuerySubstitutionsForProducts = (products: ProductForSubstitutions[]) => {
  const storeParams = useStoreParams()
  const userId = useUserId()
  const { store_location_id } = storeParams
  // filter out special request products and sort by product id
  const productIds = getSortedProductIds(products)

  return useQuery({
    ...substitutionsForProductsQueryOptions({ storeParams, productIds }),
    enabled: Boolean(userId && store_location_id && productIds.length > 0),
  })
}
